import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import CloseIcon from '@mui/icons-material/Close'
import MultiFileInput from 'features/bpDetails/Components/files/MultiFileInput'
import FolderUploadButton from 'features/bpDetails/Components/folders/FolderUploadButton'
import CloudFilesNoticeModal from 'features/bpDetails/Components/files/CloudFilesNoticeModal'
import { partitionUploadableFiles, cloudFileName } from 'utils/cloudFiles'
import FolderStructurePreview from 'features/bpDetails/Components/folders/FolderStructurePreview'
import APIService from 'services/APIService'
import { isNilOrEmpty, isNotNil, isNotNilOrEmpty } from 'utils/ramda'
import NextButton from 'features/createBp/components/atoms/NextButton'
import FileModalTableRow from 'features/bpDetails/Components/files/FileModalTableRow'
import AssistantSteps from 'features/bpDetails/Components/files/AssistantSteps'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  addFileToProcess,
  addUnitFileToProcess,
  finalizeMultiUploadProcess,
  getFilesListForBp,
  startMultiUploadProcess,
  startUnitMultiUploadProcess,
  uploadFileViaS3
} from 'services/fileService'
import { useDispatch, useSelector } from 'react-redux'
import {
  getSelectedBp,
  selectHasAccessToBp
} from 'features/bpDetails/ducks/selectors'
import { getApiErrors } from 'utils/errors'
import { toast } from 'react-toastify'
import BulkEditForm from 'features/bpDetails/Components/files/BulkEditForm'
import {
  extendSessionRoutine,
  finalizeFileRoutine,
  getBpFilesRoutine,
  getFileTagsForBpRoutine,
  getUploadProcessRoutine
} from 'ducks/files/actions'
import {
  selectCurrentUploadProcess,
  selectMultiUploadFiles
} from 'ducks/files/selectors'
import {
  clearUploadProcessFetchDetailsRoutine,
  clearUploadProcessRoutine,
  saveUploadProcessFetchDetailsRoutine
} from 'ducks/files/actions'
import FileMinified from 'features/bpDetails/Components/FileMinified'
import Pagination from 'components/atoms/Pagination'
import UploadOnwardSharing from 'features/bpDetails/Components/files/UploadOnwardSharing'
import useMediaQuery from 'hooks/useMediaQuery'
import { isStaffUser } from 'utils/user'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import organisationService from 'services/organisationService'
import {
  fetchBpRoutine,
  markBpAsViewedRoutine
} from 'features/bpDetails/ducks/actions'
import {
  fetchBpUnitsRoutine,
  fetchUnitRoutine
} from '../../../../ducks/units/actions'
import { propOr } from 'ramda'
import { selectBpUnits } from '../../../../ducks/units/selectors'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useLocation, useNavigate, useParams } from 'react-router'
import LogoShort from 'assets/images/bp-logo-orange.png'
import { Dialog, DialogContent, DialogTitle, Button } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import BoltIcon from '@mui/icons-material/Bolt'
import { notifyUploadComplete } from 'utils/uploadEvents'
import UploadFailuresList, { completionSheetTitle, failureLabel, leaveUploadResult, resultDialogState } from 'features/bpDetails/Components/files/UploadFailuresList'
import { useUploadProcessFailures } from 'features/bpDetails/Components/files/useUploadProcessFailures'
import { RAG } from 'features/dashboard/charts/scoreColour'
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const SUPPORTED_EXTENSIONS = [
  'pdf', 'dwg', 'doc', 'docx', 'jpg', 'jpeg', 'txt', 'png',
  'gif', 'svg', 'xlsx', 'xls', 'xlsm', 'msg', 'zip'
]

const ACCEPT_STRING = SUPPORTED_EXTENSIONS.map(ext => `.${ext}`).join(',')

const getFileExtension = (fileName) => {
  const parts = fileName.split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const filterSupportedFiles = (fileList) => {
  const supported = []
  const skipped = []
  fileList.forEach(file => {
    const ext = getFileExtension(file.name || file.path || '')
    if (ext && SUPPORTED_EXTENSIONS.includes(ext)) {
      supported.push(file)
    } else {
      skipped.push(file.name || file.path || 'unknown')
    }
  })
  return { supported, skipped }
}

const AddMultipleFilesPage = ({
  externalBpId,
  children,
  unit,
  isPrivate,
  addedFilesCount
}) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const method = queryParams.get('method') || 'ai'
  const isAI = method === 'ai'
  const { id } = useParams()
  const hasFullAccess = useSelector(selectHasAccessToBp)

  const initialCategory = queryParams.get('category')
  const initialType = queryParams.get('type')
  const initialTemplateId = queryParams.get('templateId')
  const checklistTaskId = queryParams.get('taskId')
  const initialUnitsParam = queryParams.get('units')
  const initialTagsParam = queryParams.get('tags')
  const initialUserIdsParam = queryParams.get('userIds')
  const customViewId = queryParams.get('customViewId')
  const customFolderId = queryParams.get('customFolderId')
  const folderName = queryParams.get('folderName')
  const unitIdFromQuery = queryParams.get('unit')
  const isPrivateFromQuery = queryParams.get('private') === 'true'

  const api = APIService()

  const [showCompletionSummary, setShowCompletionSummary] = useState(false)
  const [completionSummary, setCompletionSummary] = useState({
    status: 'success',
    totalFiles: 0,
    successFiles: 0,
    failedFiles: 0
  })

  const [files, setFiles] = useState([])
  const [selectedFiles, setSelectedFiles] = useState([])
  const [cloudFileNames, setCloudFileNames] = useState([])
  const [folderUploadData, setFolderUploadData] = useState(null)
  const [showFolderPreview, setShowFolderPreview] = useState(false)
  const [fileFolderMap] = useState(() => new Map()) // Track file-to-folder assignments
  const [resolvedCustomViewId, setResolvedCustomViewId] = useState(null)
  const [resolvedCustomFolderId, setResolvedCustomFolderId] = useState(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [validationState, setValidationState] = useState([])
  const fileListRef = useRef(null)
  const [alreadyAddedFiles, setAlreadyAddedFiles] = useState([])
  const [showIntro, setShowIntro] = useState(isAI)
  const [currentStep, setCurrentStep] = useState(1)
  const [assistantStep, setAssistantStep] = useState(1)
  const [fileData, setFileData] = useState({})
  const [addedPagePagination, setAddedPagePagination] = useState({
    page: 1,
    pagesCount: 1
  })
  const [payload, setPayload] = useState([])
  const [onwardSharingAllowed, setOnwardSharingAllowed] = useState(true)
  const [organisationRoutes, setOrganisationRoutes] = useState(null)
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const [bulkChangeValues, setBulkChangeValues] = useState(null)
  const dispatch = useDispatch()
  const isChecklistLinkedUpload =
    isNotNilOrEmpty(checklistTaskId) || isNotNilOrEmpty(initialTemplateId)
  const initialUnitIds = useMemo(() => {
    const parsed = (initialUnitsParam || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean)

    if (parsed.length > 0) {
      return parsed
    }

    return unitIdFromQuery && isChecklistLinkedUpload ? [unitIdFromQuery] : []
  }, [initialUnitsParam, unitIdFromQuery, isChecklistLinkedUpload])
  const initialTagIds = useMemo(
    () =>
      (initialTagsParam || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
    [initialTagsParam]
  )
  const initialUserIds = useMemo(
    () =>
      (initialUserIdsParam || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
    [initialUserIdsParam]
  )
  const resolvedUnit =
    unit ||
    (!isChecklistLinkedUpload && unitIdFromQuery
      ? { id: unitIdFromQuery }
      : null)
  const resolvedIsPrivate = isPrivate || isPrivateFromQuery
  const isUnit = isNotNilOrEmpty(resolvedUnit)
  const [isLoading, setLoading] = useState(false)
  const multiUploadFiles = useSelector(selectMultiUploadFiles)
  const [followProcess, setFollowProcess] = useState(null)
  const { failures: processFailures, checking: checkingProcess } = useUploadProcessFailures(followProcess, multiUploadFiles)
  const filesFromLocalStorage = JSON.parse(
    localStorage.getItem('uploadFiles') || '[]'
  ).map(f => f.trim())
  const usedMultiUploadFiles = new Set()
  const buildingName = bp?.name || 'Building Passport'
  const bpUnits = useSelector(selectBpUnits)
  const checklistUnitNames = initialUnitIds
    .map(unitId => bpUnits.find(u => u.id === unitId)?.name)
    .filter(Boolean)
  const unitName = isUnit
    ? (bpUnits.find(u => u.id === resolvedUnit.id)?.name || 'Unit')
    : null
  const uploadContext = isUnit
    ? `${unitName} (from ${buildingName})${resolvedIsPrivate ? ' · Private' : ' · General'}`
    : isChecklistLinkedUpload && checklistUnitNames.length > 0
      ? `${checklistUnitNames[0]} (from ${buildingName})${resolvedIsPrivate ? ' · Private' : ' · General'}`
      : buildingName
  const [isFetched, setIsFetched] = useState(false)
  const [fileCountWarning, setFileCountWarning] = useState(null)
  const [preserveFolderStructure, setPreserveFolderStructure] = useState(true)
  const [zipRootFolderName, setZipRootFolderName] = useState('')
  const [accumulatedFolders, setAccumulatedFolders] = useState([])
  const [stripRootFolders, setStripRootFolders] = useState(true)
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, total: 0 })

  const isZipOnly = useMemo(() => {
    if (files.length === 0) return false
    return files.every(f => {
      const name = f.name || f.path || ''
      return name.toLowerCase().endsWith('.zip')
    })
  }, [files])

  const notifyChecklistTaskUploadComplete = React.useCallback(() => {
    if (!checklistTaskId) return

    const key = `checklist-task-upload-complete-${checklistTaskId}`
    const payload = JSON.stringify({
      taskId: checklistTaskId,
      completedAt: new Date().toISOString()
    })

    localStorage.setItem(key, payload)
    window.dispatchEvent(
      new StorageEvent('storage', {
        key,
        newValue: payload
      })
    )
  }, [checklistTaskId])

  const allCompleted = useMemo(() => {
    if (isNotNilOrEmpty(multiUploadFiles)) {
      return (
        multiUploadFiles.every(file =>
          ['completed', 'failed'].includes(file.status)
        ) && filesFromLocalStorage.length === multiUploadFiles.length
      )
    } else {
      return false
    }
  }, [filesFromLocalStorage, multiUploadFiles])

  useEffect(() => {
    let cancelled = false
    organisationService
      .getMine()
      .then(res => {
        if (cancelled) return
        const superAdmins = res?.data?.data?.superAdmins
        setOrganisationRoutes(Array.isArray(superAdmins) && superAdmins.length > 0)
      })
      .catch(() => {
        if (!cancelled) setOrganisationRoutes(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (files.length > 50000) {
      setFileCountWarning({ level: 'error', count: files.length })
    } else if (files.length > 5000) {
      setFileCountWarning({ level: 'warning', count: files.length })
    } else {
      setFileCountWarning(null)
    }
  }, [files.length])


  useEffect(() => {
    const interval = setInterval(
      () => {
        dispatch(extendSessionRoutine.trigger())
      },
      5 * 60 * 1000
    ) //every 5 minutes

    return () => clearInterval(interval)
  }, [dispatch])

  useEffect(() => {
    if (multiUploadFiles.length > 0 && Object.keys(fileData).length > 0) {
      Object.keys(fileData).forEach(key => {
        const fileId = fileData[key].file_id
        const serverFile = multiUploadFiles.find(f => f.id === fileId)

        if (serverFile) {
          setFileData(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              status: serverFile.status
            }
          }))
        }
      })
    }
  }, [multiUploadFiles])

  useEffect(() => {
    // Handle document visibility changes
    if (typeof document !== 'undefined') {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && currentStep === 2) {
          // When user returns to the tab and we're on step 2 (complete upload)
          // Reset loading state
          setLoading(false)

          // Check if uploads are completed
          const bpId = externalBpId || bp?.id
          if (bpId) {
            dispatch(
              getUploadProcessRoutine({
                processId: window.lastProcessId,
                bpId: bpId
              })
            )
          }
        }
      }

      // Listen for visibility change events
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Clean up
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
    return () => {}
  }, [currentStep, externalBpId, bp?.id, dispatch])

  useEffect(() => {
    if (id) {
      dispatch(getFileTagsForBpRoutine({ bpId: id }))
      if (!isFetched) {
        dispatch(
          fetchBpRoutine({
            id,
            callback: !isFetched ? () => setIsFetched(true) : null
          })
        )
        dispatch(fetchBpUnitsRoutine({ bpId: id }))
      }
    }
  }, [id, hasFullAccess])

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIntro(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const handleSelectAll = () => {
    selectedFiles.length === files.length
      ? setSelectedFiles([])
      : setSelectedFiles(files)
  }

  const handleAddedFilesFetch = page => {
    getFilesListForBp({
      id: externalBpId || bp.id,
      query: {
        page: Math.max(1, page || addedPagePagination.page),
        limit: 10,
        filter: { type: initialType, include_identity_documents: 1 }
      }
    }).then(resp => {
      setAddedPagePagination({
        pagesCount: resp.data.meta.lastPage,
        page: resp.data.meta.currentPage
      })
      setAlreadyAddedFiles(resp.data.data)
    })
  }

  const handleModalOpen = () => {
    setIsSubmitted(false)
    setTimeout(() => {
      setShowIntro(false)
    }, 500)

    if (addedFilesCount && addedFilesCount > 0) {
      handleAddedFilesFetch()
    }
  }

  const handleBulkChangeValues = val => {
    setBulkChangeValues(val)

    setTimeout(() => {
      setSelectedFiles([])
      setBulkChangeValues(null)
    }, 100)
  }

  const navigateBack = useNavigate()
  const resultState = resultDialogState({ status: completionSummary?.status, failureCount: processFailures.length, checking: checkingProcess })
  const leaveResult = () => leaveUploadResult({ closeSummary: () => setShowCompletionSummary(false), goBack: () => navigateBack(-1) })

  const startAgain = () => {
    setShowCompletionSummary(false)
    setFollowProcess(null)
    setLoading(false)
    setFiles([])
    setShowIntro(true)
    setSelectedFiles([])
    setPayload([])
    setValidationState([])
    setFileData({})
    setCurrentStep(1)
    setAssistantStep(1)
    setAccumulatedFolders([])
  }

  const handleModalClose = () => {
    setLoading(false)
    setFiles([])
    setShowIntro(true)
    setSelectedFiles([])
    setPayload([])
    setValidationState([])
    setFileData({})
    setCurrentStep(1)
    setAssistantStep(1)
    setAccumulatedFolders([])

    if (addedFilesCount && addedFilesCount > alreadyAddedFiles.length) {
      dispatch(fetchBpRoutine({ id: externalBpId || bp.id }))
    }

    window.close()
  }

  const handleAddFiles = async filesToAdd => {
    const { supported, skipped } = filterSupportedFiles(filesToAdd)
    if (skipped.length > 0) {
      toast.warn(
        `${skipped.length} file${skipped.length > 1 ? 's' : ''} skipped (unsupported format): ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? ` and ${skipped.length - 5} more` : ''}`
      )
    }
    if (supported.length === 0) return

    const { readable, unreadable } = await partitionUploadableFiles(supported)

    if (readable.length > 0) {
      setFiles(prev => [...prev, ...readable])
      setIsSubmitted(false)
      const largeZips = readable.filter(f =>
        f.name?.toLowerCase().endsWith('.zip') && f.size > 1073741824
      )
      if (largeZips.length > 0) {
        toast.warning('ZIP files over 1GB may fail during processing. Consider using folder upload instead.')
      }
    }

    if (unreadable.length > 0) {
      setCloudFileNames(unreadable.map(f => cloudFileName(f)))
    }
  }

  const handleFolderSelect = async (folderData) => {
    const { readable, unreadable } = await partitionUploadableFiles(
      folderData.files || [],
      w => w.file
    )
    if (unreadable.length > 0) {
      setCloudFileNames(unreadable.map(w => cloudFileName(w, x => x.file)))
    }
    if (readable.length === 0) return

    const filesData = {
      ...folderData,
      files: readable,
      totalFiles: readable.length,
      totalSize: readable.reduce((sum, f) => sum + (f.size || 0), 0)
    }
    const rootFolder = filesData.folderStructure[0]
    setAccumulatedFolders(prev => [...prev, {
      name: rootFolder,
      filesData
    }])
  }

  const handleRemoveAccumulatedFolder = (index) => {
    setAccumulatedFolders(prev => prev.filter((_, i) => i !== index))
  }

  const handleMultiFolderPreview = () => {
    let allFiles = []
    let allFolders = new Set()

    accumulatedFolders.forEach(({ name, filesData }) => {
      if (stripRootFolders) {
        filesData.files.forEach(f => {
          const stripped = f.path.startsWith(name + '/') ? f.path.slice(name.length + 1) : f.path
          allFiles.push({ ...f, path: stripped })
        })
        filesData.folderStructure.forEach(p => {
          if (p === name) return
          const stripped = p.startsWith(name + '/') ? p.slice(name.length + 1) : p
          if (stripped) allFolders.add(stripped)
        })
      } else {
        allFiles.push(...filesData.files)
        filesData.folderStructure.forEach(p => allFolders.add(p))
      }
    })

    setFolderUploadData({
      files: allFiles,
      folderStructure: [...allFolders].sort(),
      totalFiles: allFiles.length,
      totalSize: allFiles.reduce((sum, f) => sum + f.size, 0)
    })
    setShowFolderPreview(true)
  }

  const handleFolderPreviewConfirm = async (confirmData) => {
    setShowFolderPreview(false)

    try {
      let folderIdMap = {}
      let resultCustomViewId = null
      let customFolderIdMap = {}

      // Use user-provided view name, falling back to folder structure
      const rootFolderName = confirmData.newViewName ||
        confirmData.folders?.[0]?.path?.split('/')[0] ||
        confirmData.filesData.files[0]?.path?.split('/')[0] ||
        'Uploaded Folder'

      // Use query param IDs, falling back to user-selected IDs from the preview dialog
      const effectiveCustomViewId = customViewId || confirmData.selectedCustomViewId
      const effectiveCustomFolderId = customFolderId || confirmData.selectedCustomFolderId
      // Store in state so handleFileUpload can access them
      setResolvedCustomViewId(effectiveCustomViewId)
      setResolvedCustomFolderId(effectiveCustomFolderId)

      // Create folders (or get existing) - this also creates CustomView and CustomFolders
      if (confirmData.folders && confirmData.folders.length > 0) {
        const requestData = {
          folders: confirmData.folders,
          rootFolderName: rootFolderName
        }

        // If we're uploading inside an existing custom folder, pass the existing IDs
        if (effectiveCustomViewId) {
          requestData.custom_view_id = effectiveCustomViewId
        }
        if (effectiveCustomFolderId) {
          requestData.target_custom_folder_id = effectiveCustomFolderId
          // Pass whether to preserve root folder or add contents directly
          requestData.preserve_root_folder = confirmData.preserveRootFolder !== false
        }

        const response = await api.post(
          `/building-passport/${bp.id}/folders/batch-create`,
          requestData
        )
        folderIdMap = response.data.folders
        resultCustomViewId = response.data.customViewId
        // Convert array format back to object (array format prevents axios-case-converter from mangling keys)
        const customFoldersArray = response.data.customFolders || []
        customFolderIdMap = {}
        customFoldersArray.forEach(item => {
          customFolderIdMap[item.path] = item.id
        })
      }

      // Use the result customViewId, or fallback to the effective one (query params or user selection)
      const finalCustomViewId = resultCustomViewId || effectiveCustomViewId

      // Get existing folder IDs for folders that already exist
      // Pass custom_view_id to also get CustomFolder path mappings
      const existingFoldersResponse = await api.get(
        `/building-passport/${bp.id}/folders/paths`,
        { params: { custom_view_id: finalCustomViewId } }
      )
      const existingFolderIdMap = existingFoldersResponse.data.folders
      // Convert array format back to object (array format prevents axios-case-converter from mangling keys)
      const existingCustomFoldersArray = existingFoldersResponse.data.customFolders || []
      const existingCustomFolderIdMap = {}
      existingCustomFoldersArray.forEach(item => {
        existingCustomFolderIdMap[item.path] = item.id
      })

      // Merge both maps (existing + newly created)
      const completeFolderIdMap = { ...existingFolderIdMap, ...folderIdMap }

      // For existingCustomFolderIdMap, keys are full paths like "F676/4) Drawings/UploadedFolder"
      // But fileToFolderMap has relative paths like "UploadedFolder/SubFolder"
      // We need to create mappings that match the upload folder structure

      // First, find the target folder's full path (if uploading into existing folder)
      let targetFolderPrefix = ''
      if (effectiveCustomFolderId && Object.keys(existingCustomFolderIdMap).length > 0) {
        // Find the path that maps to the target folder ID
        const targetEntry = Object.entries(existingCustomFolderIdMap).find(
          ([path, id]) => id === effectiveCustomFolderId
        )
        if (targetEntry) {
          targetFolderPrefix = targetEntry[0] + '/'  // e.g., "F676/4) Drawings/"
        }
      }

      const normalizedExistingMap = {}
      Object.entries(existingCustomFolderIdMap).forEach(([fullPath, folderId]) => {
        // Keep the full path mapping
        normalizedExistingMap[fullPath] = folderId

        // MOST IMPORTANT: If this path is under the target folder,
        // create a relative path WITHOUT the target prefix
        // This matches the format of fileToFolderMap paths
        if (targetFolderPrefix && fullPath.startsWith(targetFolderPrefix)) {
          const relativeToTarget = fullPath.slice(targetFolderPrefix.length)
          if (relativeToTarget && !normalizedExistingMap[relativeToTarget]) {
            normalizedExistingMap[relativeToTarget] = folderId
          }
        }

        // Also add relative paths by stripping each segment from the front
        // This handles other edge cases
        const parts = fullPath.split('/')
        for (let i = 1; i < parts.length; i++) {
          const relativePath = parts.slice(i).join('/')
          // Don't override if already exists (prefer shorter/more specific paths)
          if (!normalizedExistingMap[relativePath]) {
            normalizedExistingMap[relativePath] = folderId
          }
        }
      })

      const completeCustomFolderIdMap = { ...normalizedExistingMap, ...customFolderIdMap }

      // Filter out unsupported file formats from folder upload
      const supportedFileData = confirmData.filesData.files.filter(fileData => {
        const ext = getFileExtension(fileData.name || fileData.file?.name || '')
        return ext && SUPPORTED_EXTENSIONS.includes(ext)
      })
      const skippedFromFolder = confirmData.filesData.files.length - supportedFileData.length
      if (skippedFromFolder > 0) {
        toast.warn(
          `${skippedFromFolder} file${skippedFromFolder > 1 ? 's' : ''} skipped from folder (unsupported format)`
        )
      }

      const filesWithFolders = supportedFileData.map(fileData => {
        const folderPath = confirmData.fileToFolderMap[fileData.path]
        const folderId = folderPath ? completeFolderIdMap[folderPath] : null

        // Try exact match first, then case-insensitive search
        let mappedCustomFolderId = null
        if (folderPath) {
          mappedCustomFolderId = completeCustomFolderIdMap[folderPath]

          // If exact match fails, try case-insensitive search
          if (!mappedCustomFolderId) {
            const lowerFolderPath = folderPath.toLowerCase()
            const matchingKey = Object.keys(completeCustomFolderIdMap).find(
              key => key.toLowerCase() === lowerFolderPath
            )
            if (matchingKey) {
              mappedCustomFolderId = completeCustomFolderIdMap[matchingKey]
            }
          }

        }

        const conflictResolution =
          confirmData.conflictResolutions?.[fileData.path] || null

        // Store folder mapping in Map (File objects can't be reliably mutated)
        const file = fileData.file
        fileFolderMap.set(file, {
          bpFolderId: folderId,
          customViewId: finalCustomViewId,
          customFolderId: mappedCustomFolderId,
          originalFolderPath: fileData.path,
          conflictResolution: conflictResolution
        })

        return file
      })

      // Add files to the files list (no re-filtering needed, already filtered above)
      setFiles(prev => [...prev, ...filesWithFolders])
      setIsSubmitted(false)
      setAccumulatedFolders([])

      // Initialize payload and validation state for the new files
      const newPayloadEntries = filesWithFolders.map(file => {
        const folderData = fileFolderMap.get(file) || {}
        return {
          file: file,
          displayName: file.name,
          bpFolderId: folderData.bpFolderId || null,
          customViewId: folderData.customViewId || null,
          customFolderId: folderData.customFolderId || null,
          originalFolderPath: folderData.originalFolderPath || null,
          conflictResolution: folderData.conflictResolution || null
        }
      })

      setPayload(prev => [...prev, ...newPayloadEntries])
      setValidationState(prev => [...prev, ...filesWithFolders.map(() => ({}))])

      const newFoldersCount = confirmData.folders?.length || 0
      const existingFoldersCount = Object.keys(existingFolderIdMap).length

      const addedCount = filesWithFolders.length
      const folderSummary = newFoldersCount > 0
        ? `${newFoldersCount} ${newFoldersCount === 1 ? 'folder' : 'folders'} created${existingFoldersCount > 0 ? ` and ${existingFoldersCount} reused` : ''}.`
        : 'Your files were added to the folders that already exist.'
      toast.info(
        `${folderSummary} Your ${addedCount} ${addedCount === 1 ? 'file is' : 'files are'} ready but not uploaded yet. Check the details below, then press "Upload Files" to finish.`,
        { autoClose: 10000 }
      )
    } catch (error) {
      toast.error('Failed to process folder structure. Please try again.')
    }
  }

  const handleRemove = index => () => {
    const file = files[index]
    setFiles(prev => prev.filter((_, i) => i !== index))
    setSelectedFiles(prev =>
      prev.filter(f => f.lastModified !== file.lastModified)
    )
    const tempValidationState = [...validationState]
    const tempPayload = [...payload]
    tempValidationState.splice(index, 1)
    tempPayload.splice(index, 1)
    setValidationState(tempValidationState)
    setPayload(tempPayload)
  }

  const toggleFileSelection = file => () => {
    const isSelected = isNotNilOrEmpty(
      selectedFiles.find(f => f.name === file.name)
    )
    setSelectedFiles(prev => {
      if (isSelected) {
        return prev.filter(f => f.name !== file.name)
      } else {
        return [...prev, file]
      }
    })
  }

  const handleValidationChange = index => value => {
    setValidationState(prev => {
      const newState = [...prev]
      newState[index] = value
      return newState
    })
  }

  const handleValuesChange = index => values => {
    setPayload(prev => {
      const newState = [...prev]
      const file = values.file
      // Get folder data from Map (for folder upload feature)
      const folderData = fileFolderMap.get(file) || {}
      newState[index] = {
        file: file,
        ...values.values,
        // Include folder properties from Map, preserving any existing values from form
        bpFolderId: values.values.bpFolderId || folderData.bpFolderId || null,
        customViewId: values.values.customViewId || folderData.customViewId || null,
        customFolderId: values.values.customFolderId || folderData.customFolderId || null,
        originalFolderPath: values.values.originalFolderPath || folderData.originalFolderPath || null,
        conflictResolution: values.values.conflictResolution || folderData.conflictResolution || null
      }
      return newState
    })
  }

  const normalizeFileName = name => {
    if (!name) return ''

    // First, simplify by lowercase
    let normalized = name.toLowerCase()

    // Remove common file extensions anywhere in the string
    normalized = normalized
      .replace(/\.pdf\.png$/i, '.') // Handle the specific .pdf.png case first
      .replace(/\.pdf/gi, '.') // Remove PDF extensions anywhere
      .replace(/\.(png|jpg|jpeg|doc|docx|xlsx|xls)$/i, '.') // Remove other extensions at the end
      .replace(/\./g, '') // Remove all remaining dots
      .replace(/[_\- ]/g, '') // Remove underscores, hyphens, and spaces

    return normalized
  }

  const headers = isUnit
    ? resolvedIsPrivate
      ? ['File', 'Delete']
      : !isAI
        ? isChecklistLinkedUpload
          ? ['File', 'File Type', 'Unit', 'Tags', 'Expiry', 'Delete']
          : ['File', 'File Type', 'Expiry', 'Delete']
        : isChecklistLinkedUpload
          ? ['File', 'Unit', 'Tags', 'Expiry', 'Delete']
          : ['File', 'Expiry', 'Delete']
    : resolvedIsPrivate
      ? ['File', 'Delete']
      : !isAI
        ? ['File', 'File Type', 'Units', 'Share', 'Tags', 'Expiry', 'Delete']
        : ['File', 'Units', 'Share', 'Tags', 'Expiry', 'Delete']

  const isMobile = useMediaQuery('(max-width: 768px)')
  const isStaffUploader = isStaffUser(currentUser) || currentUser?.type === 'admin'
  const showOnwardSharing = !resolvedIsPrivate && !isStaffUploader && organisationRoutes === false
  const desktopRowHeight = showOnwardSharing ? 116 : 72
  const mobileRowHeight = 112 + (headers.length - 1) * 56 + (showOnwardSharing ? 56 : 0)
  const ROW_HEIGHT = isMobile ? mobileRowHeight : desktopRowHeight
  const onwardSharingFor = file => {
    if (!showOnwardSharing) return undefined
    return file.onwardSharingOverride ?? onwardSharingAllowed
  }

  const renderRow = useCallback(({ index, style }) => {
    const file = files[index]
    const findInSelected = selectedFiles.find(f => f.name === file.name)
    const isSelected = isNotNilOrEmpty(findInSelected)

    const mappingKey = `file-${index}`
    const fileMapping = fileData[mappingKey]

    let uploadStatus = 'pending'
    let matchingFile = null

    if (fileMapping) {
      matchingFile = multiUploadFiles.find(f => f.id === fileMapping.file_id)

      if (matchingFile && !usedMultiUploadFiles.has(matchingFile.id)) {
        usedMultiUploadFiles.add(matchingFile.id)
        uploadStatus = matchingFile.status
      }
    }

    return (
      <div style={style}>
        <FileModalTableRow
          isPrivate={resolvedIsPrivate}
          unit={resolvedUnit}
          isChecklistLinkedUpload={isChecklistLinkedUpload}
          currentStep={currentStep}
          isAI={isAI}
          initialCategory={
            matchingFile?.storedFile &&
            (matchingFile.storedFile.use_ai || matchingFile.storedFile.useAi) &&
            (matchingFile.storedFile.ai_confidence > 55 ||
              matchingFile.storedFile.aiConfidence > 55) &&
            (matchingFile.storedFile.ai_file_category_id ||
              matchingFile.storedFile.aiFileCategoryId)
              ? matchingFile.storedFile.ai_file_category_id ||
                matchingFile.storedFile.aiFileCategoryId
              : initialCategory || null
          }
          initialType={
            matchingFile?.storedFile &&
            (matchingFile.storedFile.use_ai || matchingFile.storedFile.useAi) &&
            (matchingFile.storedFile.ai_confidence > 55 ||
              matchingFile.storedFile.aiConfidence > 55) &&
            (matchingFile.storedFile.ai_file_type_id ||
              matchingFile.storedFile.aiFileTypeId)
              ? matchingFile.storedFile.ai_file_type_id ||
                matchingFile.storedFile.aiFileTypeId
              : initialType || null
          }
          initialTemplateId={initialTemplateId}
          initialUnitIds={initialUnitIds}
          initialTagIds={initialTagIds}
          initialUserIds={initialUserIds}
          certaintyLevel={
            matchingFile?.storedFile &&
            (matchingFile.storedFile.use_ai || matchingFile.storedFile.useAi) &&
            (matchingFile.storedFile.ai_confidence ||
              matchingFile.storedFile.aiConfidence)
              ? matchingFile.storedFile.ai_confidence ||
                matchingFile.storedFile.aiConfidence
              : 0
          }
          createdAt={
            matchingFile?.storedFile &&
            (matchingFile.storedFile.created_at ||
              matchingFile.storedFile.createdAt)
              ? matchingFile.storedFile.created_at ||
                matchingFile.storedFile.createdAt
              : null
          }
          thumbnailUrl={
            matchingFile?.storedFile &&
            (matchingFile.storedFile.thumbnail_url ||
              matchingFile.storedFile.thumbnailUrl)
              ? matchingFile.storedFile.thumbnail_url ||
                matchingFile.storedFile.thumbnailUrl
              : null
          }
          isSelected={isSelected}
          isSubmitted={isSubmitted}
          bulkChangeValues={bulkChangeValues}
          showOnwardSharing={showOnwardSharing}
          batchOnwardSharing={onwardSharingAllowed}
          key={`file-table-row-${file.name}`}
          file={file}
          onRemove={handleRemove(index)}
          onSelect={toggleFileSelection(file)}
          onValidationStateChange={handleValidationChange(index)}
          onChange={handleValuesChange(index)}
          uploadStatus={uploadStatus}
        />
      </div>
    )
  }, [files, selectedFiles, fileData, multiUploadFiles, resolvedIsPrivate, resolvedUnit, currentStep, isAI, initialCategory, initialType, isSubmitted, bulkChangeValues, showOnwardSharing, onwardSharingAllowed, ROW_HEIGHT])

  const virtualizedListHeight = useMemo(() => {
    const naturalHeight = files.length * ROW_HEIGHT
    const maxHeight = 500
    return Math.min(naturalHeight, maxHeight)
  }, [files.length, ROW_HEIGHT])

  const selectedFilesLength = selectedFiles.map((_, i) => i).length

  const isFormValid = validationState.every(v => v)

  useEffect(() => {
    if (isNilOrEmpty(files) || showCompletionSummary) return

    const handleBeforeUnload = event => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [files, showCompletionSummary])

  const handleFileUpload = () => {
    setIsSubmitted(true)
    const bpId = externalBpId || bp.id

    if (!isFormValid && !resolvedIsPrivate) {
      const incompleteCount = validationState.filter(v => !v).length
      const firstIncompleteIndex = validationState.findIndex(v => !v)
      toast.error(
        incompleteCount === 1
          ? 'One file still needs its details before it can be uploaded. We have scrolled down to it for you.'
          : `${incompleteCount} files still need their details before they can be uploaded. We have scrolled down to the first one for you.`
      )
      if (firstIncompleteIndex >= 0 && fileListRef.current) {
        fileListRef.current.scrollToItem(firstIncompleteIndex, 'center')
      }
      return
    }

    if (isFormValid || resolvedIsPrivate) {
      setUploadProgress({ uploaded: 0, total: 0 })
      setLoading(true)
      dispatch(clearUploadProcessFetchDetailsRoutine())
      dispatch(clearUploadProcessRoutine())

      if (isUnit) {
        startUnitMultiUploadProcess({
          bpId,
          unitId: resolvedUnit.id,
          totalFiles: payload.length
        }).then(resp => {
          const processId = resp.data?.data?.id
          dispatch(
            saveUploadProcessFetchDetailsRoutine({
              processId,
              unitId: resolvedUnit.id,
              bpId
            })
          )
          let successCount = 0
          let failedCount = 0

          const UNIT_BATCH_SIZE = 5
          setUploadProgress({ uploaded: 0, total: payload.length })

          const uploadUnitFiles = async () => {
            for (let i = 0; i < payload.length; i += UNIT_BATCH_SIZE) {
              const batch = payload.slice(i, i + UNIT_BATCH_SIZE)
              await Promise.all(
                batch.map(file =>
                  addUnitFileToProcess({
                    ...file,
                    bpId,
                    unitId: resolvedUnit.id,
                    processId,
                    isPrivate: resolvedIsPrivate,
                    isAI,
                    taskId: checklistTaskId,
                    onwardSharingAllowed: onwardSharingFor(file)
                  })
                    .then(() => {
                      successCount++
                      setUploadProgress(prev => ({ ...prev, uploaded: prev.uploaded + 1 }))
                    })
                    .catch(e => {
                      failedCount++
                      setUploadProgress(prev => ({ ...prev, uploaded: prev.uploaded + 1 }))
                      toast.error(getApiErrors(e))
                    })
                )
              )
            }

            setCompletionSummary({
              status: failedCount === payload.length ? 'failed' : failedCount > 0 ? 'partial' : 'success',
              totalFiles: payload.length,
              successFiles: successCount,
              failedFiles: failedCount,
              message:
                'Your files have been uploaded successfully and are now being processed in the background. You can safely close this window.'
            })
            setShowCompletionSummary(true)
            setLoading(false)
            if (processId) setFollowProcess({ processId, bpId, unitId: resolvedUnit.id })
            notifyChecklistTaskUploadComplete()
          }

          uploadUnitFiles().catch(e => {
            toast.error(getApiErrors(e))
            setLoading(false)
          })
        })
      } else {
        startMultiUploadProcess({
          bpId,
          totalFiles: payload.length,
          customViewId: resolvedCustomViewId || customViewId,
          customFolderId: resolvedCustomFolderId || customFolderId,
          preserveFolderStructure: isZipOnly && preserveFolderStructure,
          rootFolderName: isZipOnly && preserveFolderStructure ? (zipRootFolderName || undefined) : undefined
        })
          .then(resp => {
            const processId = resp.data?.data?.id
            const processFiles = resp.data?.data?.files || []

            // Save process details to Redux
            dispatch(
              saveUploadProcessFetchDetailsRoutine({
                processId,
                bpId
              })
            )

            let successCount = 0
            let failedCount = 0

            const uploadedFiles = []
            const BATCH_SIZE = 5
            setUploadProgress({ uploaded: 0, total: payload.length })

            const uploadSingleFile = async (file, index) => {
              try {
                const response = await uploadFileViaS3({
                  ...file,
                  bpId,
                  processId,
                  isAI,
                  taskId: checklistTaskId,
                  onwardSharingAllowed: onwardSharingFor(file)
                })
                const processData = response.data.data
                successCount++
                setUploadProgress(prev => ({ ...prev, uploaded: prev.uploaded + 1 }))

                if (processData && processData.files) {
                  const uploadFileName = file.file
                    ? file.file.name
                    : file.name

                  let addedFile = null
                  let matchReason = ''

                  const exactNameMatch = processData.files.find(f => {
                    const serverFileName = f.data?.displayName
                    return serverFileName === uploadFileName
                  })

                  if (exactNameMatch) {
                    addedFile = exactNameMatch
                    matchReason = 'exact name match'
                  } else {
                    const normalizedMatch = processData.files.find(f => {
                      const serverFileName = f.data?.displayName
                      if (uploadFileName && serverFileName) {
                        const normalized1 = normalizeFileName(serverFileName)
                        const normalized2 = normalizeFileName(uploadFileName)
                        return normalized1 === normalized2
                      }
                      return false
                    })

                    if (normalizedMatch) {
                      addedFile = normalizedMatch
                      matchReason = 'normalized name match'
                    } else {
                      addedFile =
                        processData.files[processData.files.length - 1]
                      matchReason = 'fallback to last file'
                    }
                  }

                  if (addedFile) {
                    const mappingData = {
                      multi_upload_id: processData.id,
                      file_id: addedFile.id,
                      file_index: index,
                      original_name: uploadFileName,
                      server_name: addedFile.data?.displayName || '',
                      match_reason: matchReason
                    }

                    setFileData(prev => ({
                      ...prev,
                      [`file-${index}`]: mappingData
                    }))

                    uploadedFiles.push({
                      ...addedFile,
                      original_index: index,
                      original_name: uploadFileName
                    })
                  }
                }
              } catch (e) {
                failedCount++
                setUploadProgress(prev => ({ ...prev, uploaded: prev.uploaded + 1 }))
                toast.error(getApiErrors(e))
              }
            }

            // Upload in batches of BATCH_SIZE to prevent browser memory exhaustion
            const uploadAllFiles = async () => {
              for (let i = 0; i < payload.length; i += BATCH_SIZE) {
                const batch = payload.slice(i, i + BATCH_SIZE)
                await Promise.all(
                  batch.map((file, batchIndex) =>
                    uploadSingleFile(file, i + batchIndex)
                  )
                )
              }

              toast.success('All files have been queued for processing')

              setCompletionSummary({
                status: 'success',
                totalFiles: payload.length,
                successFiles: successCount,
                failedFiles: failedCount,
                message:
                  'Your files have been uploaded successfully and are now being processed in the background. You can safely close this window.'
              })

              setShowCompletionSummary(true)
              setLoading(false)

              notifyUploadComplete(bpId, { count: successCount, names: payload.map(item => item?.displayName || item?.name).filter(Boolean) })
              if (processId) setFollowProcess({ processId, bpId })
              notifyChecklistTaskUploadComplete()
            }

            uploadAllFiles().catch(e => {
              toast.error(getApiErrors(e))
              setLoading(false)
            })
          })
          .catch(e => {
            toast.error(getApiErrors(e))
            setLoading(false)
          })
      }
    }
  }

  const handleSubmit = async () => {
    setIsSubmitted(true)
    dispatch(clearUploadProcessFetchDetailsRoutine())
    dispatch(clearUploadProcessRoutine())
    const bpId = externalBpId || bp.id

    if (isFormValid) {
      setLoading(true)
      setAssistantStep(4)
      toast.info('Finalising file upload, please wait…')

      // Create array of files to process
      const filesToProcess = []

      // First gather all valid files to process
      payload.forEach((file, index) => {
        const mappingKey = `file-${index}`
        const mapping = fileData[mappingKey]

        if (!mapping || !mapping.file_id) {
          return
        }

        const matchingFile = multiUploadFiles.find(
          f => f.id === mapping.file_id
        )

        if (matchingFile?.storedFile?.id) {
          filesToProcess.push({
            file,
            index,
            matchingFile,
            mapping
          })
        }
      })

      if (filesToProcess.length === 0) {
        toast.error('No valid files to finalise')
        setLoading(false)
        return
      }

      // Process files sequentially instead of in parallel
      let processedCount = 0
      const finalizedFiles = []
      const failedFiles = []

      for (const item of filesToProcess) {
        try {
          const finalizePayload = {
            fileId: item.matchingFile.storedFile.id,
            fileType: item.file.fileType,
            floors: item.file.floors,
            units: item.file.units,
            expirationDate: item.file.expirationDate,
            userIds: item.file.userIds,
            tagIds: item.file.tagIds,
            bpId: bpId,
            taskId: checklistTaskId
          }

          // Dispatch and wait (using timeout as a workaround since dispatch doesn't return a promise)
          dispatch(finalizeFileRoutine(finalizePayload))

          // Wait a bit for the request to complete
          await new Promise(resolve => setTimeout(resolve, 1500))

          finalizedFiles.push(item)
          processedCount++

          // Show progress
          if (
            processedCount % 5 === 0 ||
            processedCount === filesToProcess.length
          ) {
            toast.info(
              `Processed ${processedCount} of ${filesToProcess.length} files...`
            )
          }
        } catch (error) {
          console.error(`Error finalizing file at index ${item.index}:`, error)
          toast.error(getApiErrors(error) || 'Failed to finalise uploaded file')
          dispatch(finalizeFileRoutine.failure(error))
          failedFiles.push(item)
        }
      }

      // Check finalization status
      if (finalizedFiles.length === filesToProcess.length) {
        // All files were processed successfully
        toast.success('All files finalised successfully')

        // Update the file list in Redux before redirecting
        dispatch(getBpFilesRoutine({ id: bpId }))

        // Wait a moment for the file list to update
        await new Promise(resolve => setTimeout(resolve, 1000))

        setCompletionSummary({
          status: 'success',
          totalFiles: filesToProcess.length,
          successFiles: finalizedFiles.length,
          failedFiles: 0
        })
        notifyUploadComplete(bpId, { count: finalizedFiles.length })
        notifyChecklistTaskUploadComplete()
        setShowCompletionSummary(true)
        setLoading(false)
      } else if (finalizedFiles.length > 0) {
        // Some files were processed
        toast.warning(
          `Finalized ${finalizedFiles.length} out of ${filesToProcess.length} files`
        )

        // Update the file list for the successful ones
        dispatch(getBpFilesRoutine({ id: bpId }))

        // Wait a moment for the file list to update
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Redirect
        toast.success(
          'Upload process has been completed with some problems. You can safely close this tab.'
        )
        setCompletionSummary({
          status: 'partial',
          totalFiles: filesToProcess.length,
          successFiles: finalizedFiles.length,
          failedFiles: filesToProcess.length - finalizedFiles.length
        })
        notifyUploadComplete(bpId, { count: finalizedFiles.length })
        notifyChecklistTaskUploadComplete()
        setShowCompletionSummary(true)
        setLoading(false)
      } else {
        // No files were processed successfully
        toast.error('Failed to finalise any files')
        setCompletionSummary({
          status: 'failed',
          totalFiles: filesToProcess.length,
          successFiles: 0,
          failedFiles: filesToProcess.length
        })
        setShowCompletionSummary(true)
        setLoading(false)
      }
    }
  }

  const handleBulkDelete = () => {
    const selectedIds = new Set(selectedFiles.map(file => file.lastModified))
    const keepIndices = files
      .map((file, i) => selectedIds.has(file.lastModified) ? -1 : i)
      .filter(i => i !== -1)
    setFiles(prev => prev.filter((_, i) => keepIndices.includes(i)))
    setPayload(prev => prev.filter((_, i) => keepIndices.includes(i)))
    setValidationState(prev => prev.filter((_, i) => keepIndices.includes(i)))
    setSelectedFiles([])
  }

  if (!isFetched) {
    return (
      <Loading>
        <img src={LogoShort} alt='bp-logo' />
        <div>Loading Building Passport</div>
      </Loading>
    )
  }

  return (
    <>
      {showIntro && isAI ? (
        <>
          <ModalHeader>
            <ModalTitle>
              <AIIcon />
              Smart Upload Assistant - {uploadContext}
            </ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div>
              Welcome to your building's digital librarian and smart assistant.
              Transform your disorganised, disparate building files into a
              clean, organised and searchable repository at the drag and drop of
              your cursor.
            </div>
            <AssistantStepsWrapper>
              <AssistantSteps />
            </AssistantStepsWrapper>
          </ModalContent>
        </>
      ) : (
        <>
          <ModalHeader>
            {isAI ? (
              <ModalTitle>
                <div>
                  <AIIcon />
                  Smart Upload Assistant - {uploadContext}
                </div>
                {folderName && (
                  <FolderDestination>
                    <strong>{folderName}</strong>
                  </FolderDestination>
                )}
              </ModalTitle>
            ) : (
              <ModalTitle>
                <div>
                  <TraditionalIcon />
                  Traditional File Upload - {uploadContext}
                </div>
                {folderName && (
                  <FolderDestination>
                    <strong>{folderName}</strong>
                  </FolderDestination>
                )}
              </ModalTitle>
            )}
            {isAI && (
              <StepsWrapper>
                <AssistantSteps isSmall={true} activeStep={assistantStep} />
              </StepsWrapper>
            )}
          </ModalHeader>
          <ModalContent>
            {isAI && currentStep === 1 && (
              <FeedbackNote>
                <FeedbackNoteHeading>AI Document Upload</FeedbackNoteHeading>
                <FeedbackNoteParagraph>
                  Welcome to your building's digital librarian and smart
                  assistant.
                </FeedbackNoteParagraph>
                <FeedbackNoteParagraph>
                  Transform your disorganised, disparate building files into a
                  clean, organised and searchable repository at the drag and
                  drop of your cursor.
                </FeedbackNoteParagraph>
              </FeedbackNote>
            )}

            {!isAI && (
              <FeedbackNote>
                <FeedbackNoteHeading>Traditional Upload</FeedbackNoteHeading>
                <FeedbackNoteParagraph>
                  Upload your files and manually categorise them. You'll need to
                  select the appropriate file type and category for each
                  document.
                </FeedbackNoteParagraph>
              </FeedbackNote>
            )}
            {isAI && currentStep === 2 && (
              <FeedbackNote>
                <FeedbackNoteHeading>Review & Feedback</FeedbackNoteHeading>
                <FeedbackNoteParagraph>
                  Please review the files you have uploaded and ensure that the
                  information is correct. We need your feedback: if the file type
                  is not automatically recognised or is missing, please manually
                  add it and verify that the selected category is correct.
                </FeedbackNoteParagraph>
                <FeedbackNoteParagraph>
                  You can edit multiple files at once by selecting them from the
                  checkboxes on the left.
                </FeedbackNoteParagraph>
                <FeedbackNoteSmall>
                  Note: Only a few file types are supported by our AI system at
                  the moment, so your input is very important.
                </FeedbackNoteSmall>
              </FeedbackNote>
            )}
            {fileCountWarning && (
              <FileCountWarningBanner level={fileCountWarning.level}>
                <WarningIcon style={{ fontSize: 20, flexShrink: 0 }} />
                <span>
                  {fileCountWarning.level === 'error'
                    ? `Uploading ${fileCountWarning.count} files may cause performance issues on some devices. We recommend uploading in smaller batches or using a SharePoint connection to sync files automatically.`
                    : `You're uploading ${fileCountWarning.count} files. For best performance, consider uploading in batches of 500 or setting up a SharePoint connection to keep your files in sync automatically.`}
                </span>
              </FileCountWarningBanner>
            )}
            {isNotNilOrEmpty(files) && (
              <>
                <TableContent>
                  <TableHeaders
                    isUnit={isUnit}
                    isPrivate={resolvedIsPrivate}
                    currentStep={currentStep}
                    isAI={isAI}
                    isChecklistLinkedUpload={isChecklistLinkedUpload}
                  >
                    <TableHeader key={`header-select-all`}>
                      <RadioCell>
                        <input
                          type='checkbox'
                          onChange={handleSelectAll}
                          checked={selectedFiles.length === files.length}
                        />
                      </RadioCell>
                    </TableHeader>
                    {headers.map(header => (
                      <TableHeader
                        key={`header-${header}`}
                        className={
                          header === 'Expiry' || header === 'File'
                            ? 'date-header'
                            : ''
                        }
                      >
                        {header}
                      </TableHeader>
                    ))}
                  </TableHeaders>
                  {selectedFiles.length > 0 && (
                    <BulkEditForm
                      open={selectedFiles.length > 0}
                      onChange={handleBulkChangeValues}
                      isUnit={isUnit}
                      isPrivate={resolvedIsPrivate}
                      isAI={isAI}
                    />
                  )}
                  <div style={{ height: virtualizedListHeight }}>
                    <AutoSizer>
                      {({ width }) => (
                        <FixedSizeList
                          ref={fileListRef}
                          height={virtualizedListHeight}
                          itemCount={files.length}
                          itemSize={ROW_HEIGHT}
                          width={width}
                        >
                          {renderRow}
                        </FixedSizeList>
                      )}
                    </AutoSizer>
                  </div>
                </TableContent>
                {selectedFiles.length > 0 && (
                  <SelectedFilesWrapper>
                    <SelectedFilesHeader>
                      <div>
                        <strong>{selectedFiles.length}</strong> files selected
                      </div>
                      <div>
                        <BulkActionButton onClick={handleBulkDelete} danger>
                          <DeleteOutlineOutlinedIcon />
                          Delete Selected
                        </BulkActionButton>
                      </div>
                    </SelectedFilesHeader>
                  </SelectedFilesWrapper>
                )}
              </>
            )}
            {currentStep === 1 && (
              <>
                <FileInputWrapper>
                  <UploadRecommendation>
                    <RecommendationIcon />
                    <RecommendationText>
                      <strong>Tip:</strong> Extract any ZIP/RAR archives first, then use folder upload below. You can select multiple folders one by one, and their contents will be merged. Your folder structure is preserved automatically.
                    </RecommendationText>
                  </UploadRecommendation>
                  <UploadOptionLabel>Upload folders</UploadOptionLabel>
                  <FolderUploadButton
                    onFolderSelect={handleFolderSelect}
                    disabled={isLoading}
                  />
                  {accumulatedFolders.length > 0 && (
                    <>
                      <AccumulatedFoldersList>
                        {accumulatedFolders.map((folder, index) => (
                          <AccumulatedFolderRow key={`${folder.name}-${index}`}>
                            <FolderRowInfo>
                              <FolderCheckIcon />
                              <FolderRowName>{folder.name}</FolderRowName>
                              <FolderRowMeta>
                                {folder.filesData.totalFiles} files · {formatBytes(folder.filesData.totalSize)}
                              </FolderRowMeta>
                            </FolderRowInfo>
                            <FolderRemoveButton onClick={() => handleRemoveAccumulatedFolder(index)}>
                              <CloseIcon style={{ fontSize: 16 }} />
                            </FolderRemoveButton>
                          </AccumulatedFolderRow>
                        ))}
                        <AccumulatedFoldersFooter>
                          <StripRootLabel>
                            <StripRootCheckbox
                              type='checkbox'
                              checked={stripRootFolders}
                              onChange={e => setStripRootFolders(e.target.checked)}
                            />
                            <span>Strip parent folder names</span>
                          </StripRootLabel>
                          <StripRootDescription>
                            When uploading multiple parts (e.g. &quot;Part 1&quot;, &quot;Part 2&quot;), enable this to remove the part folder and merge their contents. For example, &quot;Part 1/Documents/file.pdf&quot; becomes &quot;Documents/file.pdf&quot;.
                          </StripRootDescription>
                        </AccumulatedFoldersFooter>
                      </AccumulatedFoldersList>
                      <PreviewUploadButton onClick={handleMultiFolderPreview}>
                        Preview & Upload ({accumulatedFolders.reduce((sum, f) => sum + f.filesData.totalFiles, 0)} files)
                      </PreviewUploadButton>
                    </>
                  )}
                  <UploadDivider>
                    <UploadDividerLine />
                    <UploadDividerText>OR</UploadDividerText>
                    <UploadDividerLine />
                  </UploadDivider>
                  <UploadOptionLabel>Upload files (ZIP supported, max ~1GB)</UploadOptionLabel>
                  <MultiFileInput
                    accept={ACCEPT_STRING}
                    onAdd={handleAddFiles}
                  />
                </FileInputWrapper>
              </>
            )}
            {currentStep === 1 && isNotNilOrEmpty(files) && !resolvedIsPrivate && (
              <UploadOnwardSharing
                isStaffUploader={isStaffUploader}
                organisationRoutes={organisationRoutes}
                checked={onwardSharingAllowed}
                onChange={setOnwardSharingAllowed}
              />
            )}
            {currentStep === 1 && isNotNilOrEmpty(files) && isZipOnly && (
              <ZipOptionsWrapper>
                <ZipOptionRow>
                  <ZipCheckbox
                    type='checkbox'
                    id='preserveFolderStructure'
                    checked={preserveFolderStructure}
                    onChange={e => setPreserveFolderStructure(e.target.checked)}
                  />
                  <ZipCheckboxLabel htmlFor='preserveFolderStructure'>
                    Keep folder structure from ZIP
                  </ZipCheckboxLabel>
                </ZipOptionRow>
                {preserveFolderStructure && !customViewId && (
                  <ZipRootFolderInput
                    type='text'
                    placeholder='View name (optional)'
                    value={zipRootFolderName}
                    onChange={e => setZipRootFolderName(e.target.value)}
                  />
                )}
                <ZipSizeWarning>
                  <WarningIcon style={{ fontSize: 18, flexShrink: 0 }} />
                  ZIP files are extracted on the server and work best under 1GB. For larger uploads, use folder upload instead.
                </ZipSizeWarning>
              </ZipOptionsWrapper>
            )}
            {currentStep === 1 && isNotNilOrEmpty(files) && (
              <ButtonWrapper>
                {isLoading && uploadProgress.total > 0 && (
                  <UploadProgressWrapper>
                    <UploadProgressHeader>
                      <UploadProgressText>
                        Uploading {uploadProgress.uploaded} of {uploadProgress.total} files
                      </UploadProgressText>
                      <UploadProgressPercent>
                        {Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%
                      </UploadProgressPercent>
                    </UploadProgressHeader>
                    <UploadProgressBarTrack>
                      <UploadProgressBarFill
                        style={{ width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%` }}
                      />
                    </UploadProgressBarTrack>
                  </UploadProgressWrapper>
                )}
                {isNotNilOrEmpty(files) && !isLoading && !showCompletionSummary && (
                  <PendingUploadBanner>
                    <PendingUploadIcon />
                    <PendingUploadText>
                      <strong>
                        {files.length} {files.length === 1 ? 'file is' : 'files are'} ready to upload.
                      </strong>{' '}
                      Nothing has been uploaded yet. Press "Upload Files" below to finish.
                    </PendingUploadText>
                  </PendingUploadBanner>
                )}
                <NextButton
                  data-tour-id='upload.submit'
                  onClick={handleFileUpload}
                  disabled={isLoading}
                  isLoading={isLoading}
                >
                  Upload Files
                </NextButton>
              </ButtonWrapper>
            )}
          </ModalContent>
        </>
      )}
      {showCompletionSummary && (
        <StyledDialog
          open={showCompletionSummary}
          onClose={() => setShowCompletionSummary(false)}
          maxWidth='sm'
          fullWidth
        >
          <DialogHeader>
            {completionSummary.status === 'success' && processFailures.length === 0 ? (
              <SuccessIcon />
            ) : (
              <WarningStatusIcon />
            )}
            <DialogHeaderText>
              {completionSheetTitle(completionSummary.status, processFailures.length)}
            </DialogHeaderText>
          </DialogHeader>
          <DialogBody>
            <UploadSummary>
              <SummaryItem>
                <SummaryLabel>Total files:</SummaryLabel>
                <SummaryValue>{completionSummary.totalFiles}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Successfully queued:</SummaryLabel>
                <SummaryValue success>
                  {completionSummary.successFiles}
                </SummaryValue>
              </SummaryItem>
              {completionSummary.failedFiles > 0 && (
                <SummaryItem>
                  <SummaryLabel>Failed to queue:</SummaryLabel>
                  <SummaryValue error>
                    {completionSummary.failedFiles}
                  </SummaryValue>
                </SummaryItem>
              )}
              {checkingProcess && processFailures.length === 0 && (
                <SummaryMessage role='status'>We are now checking each file. This page will tell you if any of them could not be saved.</SummaryMessage>
              )}
              {processFailures.length > 0 && (
                <FailureSection role='alert'>
                  <FailureHeading>{failureLabel(processFailures.length)}</FailureHeading>
                  <SummaryMessage>
                    Everything else was saved. To add these files, choose them again and upload them. If it keeps happening, the reason below should help.
                  </SummaryMessage>
                  <UploadFailuresList failures={processFailures} onRetry={startAgain} retryLabel='Choose files again' />
                </FailureSection>
              )}
              {processFailures.length === 0 && (
              <SummaryMessage>
                {completionSummary.status === 'success'
                  ? 'All files have been successfully uploaded and queued for processing. Your files will be processed in the background and will be available shortly.'
                  : completionSummary.status === 'partial'
                    ? 'Some files were uploaded successfully, but others encountered issues. Successfully uploaded files will be processed in the background. You may need to retry uploading the failed files.'
                    : 'There was a problem uploading your files. Please try again or contact support if the issue persists.'}
              </SummaryMessage>
              )}
              {resultState.showKeepOpen && (
                <CloseTabMessage role='status'>
                  Please keep this page open for a moment while we check each file.
                </CloseTabMessage>
              )}
              {resultState.showSafeToClose && (
                <CloseTabMessage>
                  Your files have been uploaded to the Building Passport and will
                  continue processing even after you close this page. It is now
                  safe to close this tab.
                </CloseTabMessage>
              )}
              <SummaryActions>
                {resultState.retryIsPrimary ? (
                  <SecondaryResultButton type='button' onClick={leaveResult}>
                    Back to documents
                  </SecondaryResultButton>
                ) : (
                  <StyledButton type='button' onClick={leaveResult}>
                    Back to documents
                  </StyledButton>
                )}
              </SummaryActions>
            </UploadSummary>
          </DialogBody>
        </StyledDialog>
      )}
      <FolderStructurePreview
        open={showFolderPreview}
        onClose={() => setShowFolderPreview(false)}
        onConfirm={handleFolderPreviewConfirm}
        filesData={folderUploadData}
        buildingPassportId={bp?.id}
        customViewId={customViewId}
        isInsideCustomFolder={!!customFolderId}
        targetFolderName={folderName || 'selected folder'}
      />
      <CloudFilesNoticeModal
        open={cloudFileNames.length > 0}
        fileNames={cloudFileNames}
        onClose={() => setCloudFileNames([])}
      />
    </>
  )
}

export default AddMultipleFilesPage

const Trigger = styled.div`
  width: fit-content;
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  padding: 30px;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
`

const FakeModal = styled.div`
  width: 95vw;
  max-width: 1400px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;

  svg {
    margin-left: 0 !important;
    cursor: default;
  }

  input,
  option,
  .MuiSelect-root {
    font-size: 12px !important;
  }
`

const CloseIconWrapper = styled.div`
  width: fit-content;
  padding: 5px;
  cursor: pointer;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[600]};
  background: #fff;
  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 11;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    height: auto;
    padding: calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const ModalContent = styled.div`
  padding: 104px 24px 80px;
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: 768px) {
    padding: 140px 16px 80px;
  }
`

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-weight: bold;
  font-size: 28px;
  font-variant: all-small-caps;

  /* Keep the main title content centered */
  > div:first-child {
    display: flex;
    align-items: center;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    font-variant: normal;
    gap: 6px;
  }
`

const MigrationNotice = styled.div`
  display: flex;
  align-items: center;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 20px 24px;
  margin: 12px 20px;
  color: #856404;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);

  svg {
    font-size: 32px !important;
    margin-right: 16px !important;
    flex-shrink: 0;
  }

  strong {
    display: block;
    margin-bottom: 8px;
    font-size: 20px;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
    margin: 10px 16px;
    font-size: 14px;

    svg {
      font-size: 24px !important;
      margin-right: 10px !important;
    }

    strong {
      font-size: 16px;
      margin-bottom: 6px;
    }
  }
`

const FolderDestination = styled.div`
  font-size: 12px;
  font-weight: 400;
  font-variant: normal;
  color: #666;
  background: #f8f9fa;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    color: #495057;
    font-weight: 600;
  }

  &::before {
    content: 'Destination:';
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.5px;
    color: #6c757d;
    font-weight: 500;
  }
`

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 16px !important;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer !important;

  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`

const FileInputWrapper = styled.div`
  padding: 0 0px;
  margin: 33px 0 29px;

  @media (max-width: 768px) {
    margin: 18px 0 20px;
  }
`

const UploadRecommendation = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #e6f7f9;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 20px;
`

const PendingUploadBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 640px;
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 8px;
  }
`

const PendingUploadIcon = styled(InfoOutlinedIcon)`
  color: #d97706;
  font-size: 20px;
  flex-shrink: 0;
`

const PendingUploadText = styled.span`
  font-size: 15px;
  color: #92400e;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const RecommendationIcon = styled(BoltIcon)`
  color: #33becc;
  font-size: 20px;
  flex-shrink: 0;
`

const RecommendationText = styled.span`
  font-size: 13px;
  color: #33becc;
  line-height: 1.5;
`

const UploadOptionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const UploadDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
`

const UploadDividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`

const UploadDividerText = styled.span`
  padding: 0 16px;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1px;
`

const FileCountWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  line-height: 1.4;
  background: ${({ level }) => level === 'error' ? '#fef2f2' : '#fffbeb'};
  border: 1px solid ${({ level }) => level === 'error' ? '#fca5a5' : '#fcd34d'};
  color: ${({ level }) => level === 'error' ? '#991b1b' : '#92400e'};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 12px;
  }
`

const TableContent = styled.div`
  border: 1px solid #eaecf0;
  border-radius: 8px;
  width: 100%;
  margin-top: 30px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    border: none;
    margin-top: 16px;
  }
`

const FeedbackNote = styled.div`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-left: 4px solid ${({ theme }) => theme.colors.primary || '#33becc'};
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 18px; /* overall smaller font size */
  color: #555; /* overall text color */

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }
`

const FeedbackNoteHeading = styled.h3`
  font-size: 22px;
  margin-bottom: 8px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary || '#33becc'};

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 6px;
  }
`

const FeedbackNoteParagraph = styled.p`
  font-size: 18px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 6px;
  }
`

const FeedbackNoteSmall = styled.p`
  font-size: 14px;
  color: #555;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const TableHeaders = styled.div`
  width: 100%;
  display: grid;
  height: 44px;
  background-color: #f7f8fd;
  color: ${({ theme }) => theme.colors.trout};
  border-bottom: 1px solid #eaecf0;

  /* Traditional upload - with file type column */
  ${({ isAI, isUnit, isPrivate }) =>
    !isAI &&
    !isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px 60px 90px 50px;
    `}

  /* AI upload - no file type column */
  ${({ isAI, isUnit, isPrivate }) =>
    isAI &&
    !isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 60px 60px 60px 90px 50px;
    `}

  /* Unit uploads with traditional */
  ${({ isAI, isUnit, isPrivate }) =>
    !isAI &&
    isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px;
    `}

  ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
    !isAI &&
    isUnit &&
    !isPrivate &&
    isChecklistLinkedUpload &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px 60px 50px;
    `}

  /* Unit uploads with AI */
  ${({ isAI, isUnit, isPrivate }) =>
    isAI &&
    isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 60px 60px;
    `}

  ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
    isAI &&
    isUnit &&
    !isPrivate &&
    isChecklistLinkedUpload &&
    css`
      grid-template-columns: 40px 1fr 60px 60px 60px 50px;
    `}

  /* Private uploads */
  ${({ isPrivate }) =>
    isPrivate &&
    css`
      grid-template-columns: 40px minmax(300px, 1fr) 60px;
    `}

  @media (max-width: 1366px) {
    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px 50px 80px 50px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 50px 50px 50px 80px 50px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 50px 50px 50px 50px;
      `}
  }

  @media (max-width: 1280px) {
    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 45px 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 45px 45px 45px 45px;
      `}
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  text-transform: uppercase;
  padding: 0 5px;
  color: ${({ theme }) => theme.colors.trout};

  &.date-header {
    justify-content: flex-start;
    padding-left: 10px;
  }
`

const ErrorsWarning = styled.div`
  max-width: 500px;
  margin: 20px auto;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.error || '#e53935'};
  color: #fff;
  text-align: center;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
`

const TraditionalIcon = styled(CloudUploadIcon)`
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 10px;
  font-size: 28px;
`

const ZipOptionsWrapper = styled.div`
  padding: 14px 18px;
  margin: 0 20px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const ZipOptionRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`

const ZipCheckbox = styled.input`
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const ZipCheckboxLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
`

const ZipSizeWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
`

const ZipRootFolderInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  margin-top: 6px;
  margin-left: 24px;
  max-width: 280px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const ButtonWrapper = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey[600]};
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  z-index: 10;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.08);
`

const UploadProgressWrapper = styled.div`
  width: 100%;
  max-width: 500px;
`

const UploadProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`

const UploadProgressText = styled.span`
  font-size: 13px;
  color: #555;
`

const UploadProgressPercent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`

const UploadProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
`

const UploadProgressBarFill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary || '#f57c00'};
  border-radius: 4px;
  transition: width 0.3s ease;
`

const StepsWrapper = styled.div`
  display: flex;
  justify-content: end;
  flex-direction: row;
  gap: 25px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    gap: 12px;
  }
`
const SelectedFilesWrapper = styled.div`
  padding: 18px 24px;
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 4px;
  margin-top: 20px;
`

const SelectedFilesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const BulkActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid
    ${({ theme, danger }) =>
      danger ? theme.colors.error : theme.colors.grey[600]};
  background: white;
  color: ${({ theme, danger }) =>
    danger ? theme.colors.error : theme.colors.haiti};
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  margin-left: 8px;

  &:hover {
    background: ${({ theme, danger }) =>
      danger ? theme.colors.error : theme.colors.grey[200]};
    color: ${({ danger }) => (danger ? 'white' : 'inherit')};
  }

  svg {
    font-size: 16px;
  }
`

const SelectedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

const DeleteIcon = styled(DeleteOutlineOutlinedIcon)`
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
`

const EditIcon = styled(EditOutlinedIcon)`
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
`

const IconWrapper = styled.div`
  padding: 5px;
  border-radius: 4px;
  transition: all .3s;
  cursor: pointer;
  background-color: ${({ active, theme }) => (active ? theme.colors.lightGrey : 'transparent')};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey}
`

const RadioCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const AlreadyUploadedWrapper = styled.div`
  margin: 30px 0;
`

const AlreadyUploadedHeader = styled.div`
  font-weight: bold;
  margin-bottom: 10px;
`

const AlreadyUploadedFiles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const AIIcon = styled(AssistantOutlinedIcon)`
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 10px;
  animation: ${pulseAnimation} 2s infinite ease-in-out;

  @media (max-width: 768px) {
    margin-right: 8px;
    font-size: 22px !important;
  }
`
const AssistantStepsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 19px;
  padding: 50px 50px;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 20px 12px;
  }
`
const Loading = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 300px;

  img {
    width: 100px;
    animation: ${pulseAnimation} 2s infinite;
    margin-bottom: 20px;
  }
`
const UploadSummary = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[600]};
`

const SummaryLabel = styled.div`
  font-weight: 500;
`

const SummaryValue = styled.div`
  font-weight: 600;
  color: ${({ theme, success, error }) =>
    success
      ? theme.colors.success || 'green'
      : error
        ? theme.colors.error || 'red'
        : theme.colors.haiti};
`

const FailureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const FailureHeading = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${RAG.critical.fg};
`

const SummaryMessage = styled.div`
  margin: 20px 0;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.grey[400]};
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
`

const SummaryActions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`
const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    border-radius: 8px;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
`

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background-color: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  gap: 16px;
`

const DialogHeaderText = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
`

const DialogBody = styled.div`
  padding: 24px;
`

const SuccessIcon = styled(CheckCircleIcon)`
  color: ${({ theme }) => theme.colors.success || '#2e7d32'};
  font-size: 28px !important;
`

const WarningStatusIcon = styled(WarningIcon)`
  color: ${({ theme }) => theme.colors.warning || '#f57c00'};
  font-size: 28px !important;
`

const CloseTabMessage = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary || '#33becc'};
  background-color: ${({ theme }) => theme.colors.lightGrey || '#f1f3f5'};
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  font-weight: 500;
`

const SecondaryResultButton = styled.button`
  min-height: 44px;
  padding: 10px 20px;
  background: #ffffff;
  color: #0c1229;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`

const StyledButton = styled.button`
  min-height: 44px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.colors.primary || '#33becc'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary || '#0069d9'};
  }
`
const FileCountWarning = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 10px 16px;
  background-color: #fff3e0;
  border: 1px solid #ffe0b2;
  border-radius: 4px;
  font-size: 14px;
  color: #e65100;
`

const AccumulatedFoldersList = styled.div`
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`

const AccumulatedFolderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;

  &:last-of-type {
    border-bottom: none;
  }
`

const FolderRowInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const FolderCheckIcon = styled(CheckCircleIcon)`
  color: #22c55e;
  font-size: 18px !important;
  flex-shrink: 0;
`

const FolderRowName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FolderRowMeta = styled.span`
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
`

const FolderRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #9ca3af;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
`

const AccumulatedFoldersFooter = styled.div`
  padding: 10px 14px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`

const StripRootLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  font-weight: 500;
`

const StripRootCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const StripRootDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  margin-left: 24px;
`


const PreviewUploadButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.primary || '#33becc'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`
